/**
 * Stripe Payment Form hook
 *
 * Hook to render stripe elements for collecting a payment method, like a credit card
 *
 * The following options are required:
 *
 * - data-returnurl (string) - if the payment form has to go to stripe, what URL should the user be sent back to
 * - data-clientsecret (string) - the client secret for the Stripe SetupIntent
 * - data-focus (string) - "false" to not focus on ready. Defaults to "true"
 *
 * NOTE: A child DOM element with a classname of `stripe-payment-elements` must
 * be present. This is where the actual elements will be mounted.
 *
 * ## Example
 *
 *   <form
 *     id="stripe-payment-form"
 *     phx-hook="StripePaymentForm"
 *     data-returnurl="https://example.com/path/to/page"
 *     data-clientsecret="abc123"
 *     data-focus="false"
 *   >
 *     <div class="stripe-payment-elements"></div>
 *     <button type="submit">
 *       Save Card
 *     </button>
 *   </form>
 */
export const StripePaymentForm = {
  mounted() {
    this.initElements();
  },

  initElements() {
    const appearance = {
      variables: {
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, "system-ui", "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
      },

      rules: {
        ".Tab": {
          borderColor: "rgb(209 213 219)",
        },
        ".Tab--selected": {
          borderColor: "rgb(13 136 233)",
        },
        ".Label": {
          fontSize: "0.875rem",
          lineHeight: "1.25rem",
          fontWeight: "500",
          color: "rgb(107 114 128)",
        },
        ".Input": {
          fontSize: "0.875rem",
          lineHeight: "1.25rem",
          color: "black",
          borderRadius: "0.375rem",
          borderColor: "rgb(209 213 219)",
          padding: "0.75rem",
        },
        ".Input:focus": {
          borderColor: "rgb(37 99 235)",
          boxShadow:
            "rgb(255, 255, 255) 0px 0px 0px 0px, rgb(13, 136, 233) 0px 0px 0px 2px, rgba(0, 0, 0, 0) 0px 0px 0px 0px",
        },
      },
    };

    const elements = window.Stripe.elements({
      appearance,
      clientSecret: this.el.dataset.clientsecret,
    });

    const paymentElement = elements.create("payment");
    paymentElement.mount(".stripe-payment-elements");

    // TODO: make this an option
    paymentElement.on("ready", () => {
      setTimeout(() => {
        if (this.el.dataset.focus != "false") {
          paymentElement.focus();
        }
      }, 200);
    });

    // send up event when we change the payment type, for example when selecting
    // apple pay. Every LiveView that mounts this hook MUST handle
    // "stripe-elements-change" (a no-op is fine). Stripe fires it on mount;
    // an unhandled event crashes the LiveView and the form disappears.
    paymentElement.on("change", (e) => {
      this.pushEvent("stripe-elements-change", e);
    });

    this.el.addEventListener("submit", (e) => {
      e.preventDefault();
      this.pushEvent("setup-intent-processing", {});

      Stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: this.el.dataset.returnurl,
        },
        redirect: "if_required",
      })
        .then((si) => {
          this.pushEvent("setup-intent-update", si);
        })
        .catch((err) => {
          this.pushEvent("setup-intent-update", { error: err });
        });
    });
  },
};

export const StripeBankAccountForm = {
  mounted() {
    this.initElements();
  },

  initElements() {
    this.eventTarget = this.el.querySelector("div[x-data]");
    this.el.addEventListener("submit", (e) => {
      e.preventDefault();
      this.setLoading(true);
      this.setErrors([]);
      this.sendToStripe();
    });
  },

  setLoading(val) {
    if (this.eventTarget) {
      this.eventTarget.dispatchEvent(
        new CustomEvent("set-loading", { detail: val }),
      );
    }
  },

  setErrors(val) {
    this.errors = Array.from(val);
    if (this.eventTarget) {
      this.eventTarget.dispatchEvent(
        new CustomEvent("set-errors", { detail: val }),
      );
    }
  },

  addError(error) {
    this.errors.push(error);
    this.setErrors(this.errors);
  },

  sendToStripe() {
    const errors = new Array();
    const routing_number = this.el
      .querySelector("input[name=routing_number]")
      .value.trim();
    const account_number = this.el
      .querySelector("input[name=account_number]")
      .value.trim();

    if (routing_number.length == 0) {
      this.addError("Routing Number must not be blank");
    }
    if (account_number.length == 0) {
      this.addError("Account Number must not be blank");
    }

    if (this.errors.length > 0) {
      this.setLoading(false);
      return;
    }

    let country = this.el.querySelector("input[name=country]").value;
    if (country === "USA") {
      country = "US";
    } // Stripe required

    const bankAccountParams = { routing_number, account_number, country };

    window.Stripe.createToken("bank_account", bankAccountParams).then(
      this.handleStripeResponse.bind(this),
    );
  },

  handleStripeResponse(result) {
    if (result.token) {
      this.pushEvent("process-token", {
        token: result.token.id,
        friendly_name: this.el
          .querySelector("input[name=friendly_name]")
          .value.trim(),
      });
    } else {
      this.setLoading(false);
      this.addError(result.error.message);
    }
  },
};
